import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Trash2, PauseCircle, Pin, Type, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DataTable } from '@/components/glitch-ui/data-table';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { FieldError } from '@/components/glitch-ui/field-error';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';

import { cn, containsEmoji, getFunnelStageLabel, isBrandCampaign } from '@/lib/utils';
import { formatCompactNumber } from '@/i18n/formatters';
import { useToast } from '@/hooks/useToast';
import { headlineTypeRank, HEADLINE_TYPE_LABEL, HEADLINE_TYPE_COLOR } from '@/components/glitch-ui/headline-type-tag';
import {
  Headline,
  FunnelStage,
  CampaignData,
  CAMPAIGN_STATE,
  HeadlinePerformanceLabel,
  HeadlineWithMetrics,
  CampaignHeadlineMetricsResponse,
  AdFunnelStage,
  HeadlineType,
} from '@/lib/types';
import { pinHeadline, setHeadlineType } from '@/lib/apis';

// #5529: editable headline-type pill — a badge-styled Select of the content types, mirroring
// the competitor ProximitySelect. Legacy/reverse-synced headlines have no type → a muted
// "Set type" placeholder that opens the same dropdown.
const HEADLINE_TYPES = Object.keys(HEADLINE_TYPE_LABEL) as HeadlineType[];
function HeadlineTypeSelect({
  type,
  onChange,
  disabled,
}: {
  type: HeadlineType | null | undefined;
  onChange: (type: HeadlineType) => void;
  disabled?: boolean;
}) {
  return (
    <Select value={type ?? undefined} onValueChange={(v) => onChange(v as HeadlineType)} disabled={disabled}>
      <SelectTrigger
        title={type ? `Type: ${HEADLINE_TYPE_LABEL[type]}` : 'Set headline type'}
        className={`h-auto w-fit gap-1 rounded-full border-0 px-2.5 py-0.5 text-xs font-medium focus:ring-1 focus:ring-[#290BD9] [&>svg]:static [&>svg]:ml-0.5 [&>svg]:size-3 [&>svg]:opacity-60 ${
          type ? HEADLINE_TYPE_COLOR[type] : 'bg-gray-100 text-gray-400'
        }`}
      >
        {type ? HEADLINE_TYPE_LABEL[type] : 'Set type'}
      </SelectTrigger>
      <SelectContent>
        {HEADLINE_TYPES.map((t) => (
          <SelectItem key={t} value={t} className="text-xs">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${HEADLINE_TYPE_COLOR[t]}`}>
              {HEADLINE_TYPE_LABEL[t]}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface HeadlineManagerProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  headlines: Headline[];
  maxHeadlines: number;
  setHeadlines: React.Dispatch<React.SetStateAction<Headline[]>>;
  adState: string;
  disabled?: boolean;
  campaign: CampaignData;
  activeFunnel?: FunnelStage;
  onFunnelChange?: (funnel: FunnelStage) => void;
  campaignId: number;
  adId: number;
  highlightedIds?: Set<number>;
  metricsData?: CampaignHeadlineMetricsResponse;
  metricsLoading?: boolean;
  funnelStages?: AdFunnelStage[];
  isGeneratingInBackground?: boolean;
  showAddModal?: boolean;
  onAddModalClose?: () => void;
  duplicateTexts?: Set<string>;
}

const PERFORMANCE_BADGE_STYLES: Record<Exclude<HeadlinePerformanceLabel, 'PENDING'>, string> = {
  BEST: 'bg-green-100 text-green-800',
  GOOD: 'bg-purple-50 text-purple-600',
  LOW: 'bg-red-100 text-red-800',
  LEARNING: 'bg-amber-100 text-amber-800',
};

function PerformanceBadge({ label }: { label: HeadlinePerformanceLabel | null }) {
  if (!label) return null;
  if (label === 'PENDING') {
    return <span className="text-xs text-gray-900">Pending</span>;
  }
  const displayLabel = label.charAt(0) + label.slice(1).toLowerCase();
  return (
    <span className={cn('inline-flex items-center rounded-full px-3.5 py-0.5 text-xs font-medium', PERFORMANCE_BADGE_STYLES[label])}>
      {displayLabel}
    </span>
  );
}

type HeadlineRow = {
  headline: Headline;
  metrics?: HeadlineWithMetrics;
  isTopPerformer: boolean;
  isHighlighted: boolean;
};

// #4625-followup: monotonic counter for stamping temp ids on unsaved
// headlines. Negative so it can't collide with backend-assigned ids.
let nextTempHeadlineId = -1;
const newTempHeadlineId = () => nextTempHeadlineId--;

export function HeadlineManager({
  headlines,
  setHeadlines,
  disabled = false,
  campaign,
  activeFunnel: externalActiveFunnel,
  onFunnelChange,
  campaignId: _campaignId,
  adId: _adId,
  highlightedIds,
  metricsData,
  metricsLoading = false,
  funnelStages: funnelStageData,
  isGeneratingInBackground = false,
  showAddModal,
  onAddModalClose,
  duplicateTexts,
}: HeadlineManagerProps) {
  const { toast } = useToast();
  const posthog = usePostHog();
  const [internalActiveFunnel, setInternalActiveFunnel] = useState<FunnelStage>(FunnelStage.UPPER_FUNNEL);

  // Use external funnel state if provided, otherwise use internal state
  const activeFunnel = externalActiveFunnel !== undefined ? externalActiveFunnel : internalActiveFunnel;
  const setActiveFunnel = onFunnelChange || setInternalActiveFunnel;

  const isBrand = isBrandCampaign(campaign);
  const isLive = campaign.state === CAMPAIGN_STATE.LIVE;

  // Delete confirmation state
  const [deletingHeadline, setDeletingHeadline] = useState<Headline | null>(null);

  // Add modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addModalText, setAddModalText] = useState('');
  // Tell-on-click validation (#5857): Add stays enabled (except at the 15 cap, which an
  // always-visible banner explains); clicking it marks the textarea inline instead of toasting.
  const [addModalError, setAddModalError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (showAddModal) {
      setAddModalText('');
      setAddModalError(undefined);
      setIsAddModalOpen(true);
      onAddModalClose?.();
    }
  }, [showAddModal]);

  const normalizeHeadline = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

  const duplicateTextsRef = useRef(duplicateTexts);
  duplicateTextsRef.current = duplicateTexts;

  // Refs for stable callback access
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // #4625-followup: ref-mirror headlines and setHeadlines so changeHeadline
  // and the cell's propagate() can read the latest values without the cell
  // component identity changing on every keystroke. Without this, the
  // columns useMemo re-runs each keystroke → new HeadlineCell function ref
  // → React remounts the cell → input loses focus mid-typing.
  const headlinesRef = useRef(headlines);
  headlinesRef.current = headlines;
  const setHeadlinesRef = useRef(setHeadlines);
  setHeadlinesRef.current = setHeadlines;

  // #5857: returns null when the edit is accepted, or a short error message the cell
  // renders inline (red border + message where the counter sits) instead of a toast.
  const changeHeadline = useCallback(async (headlineToChange: Headline, value: string, originalText: string): Promise<string | null> => {
    if (originalText === value) return null;

    if (containsEmoji(value)) return 'No emojis';
    if (value.length > 30) return 'Max 30 characters';

    // Pre-check: reject within-funnel-stage duplicates before hitting the API.
    // Matches the DB constraint uix_headline_ad_funnel_stage_id_text (NULLS NOT DISTINCT).
    const funnelStageId = headlineToChange.ad_funnel_stage?.id ?? null;
    const isDuplicate = headlinesRef.current.some(
      (h) => h.id !== headlineToChange.id &&
              normalizeHeadline(h.text) === normalizeHeadline(value) &&
              (h.ad_funnel_stage?.id ?? null) === funnelStageId,
    );
    if (isDuplicate) return 'Duplicate';

    posthog?.capture('ad_headline_edited', { campaign_id: campaign?.id });
    // No setHeadlines here — keystroke propagation already updated the parent.
    return null;
    // Intentionally omit headlines / setHeadlines from deps; we read both
    // via refs above so this callback stays stable.
  }, [posthog, campaign?.id]);

  // Read setHeadlines via the ref (like changeHeadline) and keep deps empty so this callback stays
  // stable. Otherwise an unstable `setHeadlines` prop would change its identity every render, rebuild
  // the `columns` memo, swap the HeadlineCell component type, and remount the inline input —
  // dropping focus mid-typing. #4625-followup.
  const handlePinHeadline = useCallback((headline: Headline, position: number | null) => {
    const previousPosition = headline.pin_position ?? null;
    setHeadlinesRef.current((prev) => prev.map((h) => (h.id && h.id === headline.id ? { ...h, pin_position: position } : h)));
    if (headline.id) {
      pinHeadline(headline.id, position).catch((err) => {
        console.error('Pin headline failed:', err);
        setHeadlinesRef.current((prev) => prev.map((h) => (h.id && h.id === headline.id ? { ...h, pin_position: previousPosition } : h)));
        toastRef.current({ description: 'Failed to update headline pin.', variant: 'destructive' });
      });
    }
  }, []);

  // #5529: set/edit a headline's content type — optimistic, persisted via the type endpoint,
  // rolled back on failure (mirrors handlePinHeadline). Ref-stable for the same reason as above.
  const handleHeadlineTypeChange = useCallback((headline: Headline, type: HeadlineType | null) => {
    if (!headline.id) return;
    const previousType = headline.type ?? null;
    if (type === previousType) return;
    setHeadlinesRef.current((prev) => prev.map((h) => (h.id === headline.id ? { ...h, type } : h)));
    setHeadlineType(headline.id, type).catch((err) => {
      console.error('Set headline type failed:', err);
      setHeadlinesRef.current((prev) => prev.map((h) => (h.id === headline.id ? { ...h, type: previousType } : h)));
      toastRef.current({ description: 'Failed to update headline type.', variant: 'destructive' });
    });
  }, []);

const hasMetadata = !isBrand && headlines.some((h) => h.ad_funnel_stage);

  const funnelStages = Object.values(FunnelStage);

  // Build a lookup map: headline_id -> metrics
  const metricsMap = useMemo(() => {
    const map = new Map<number, HeadlineWithMetrics>();
    if (metricsData?.headlines) {
      for (const m of metricsData.headlines) {
        map.set(m.headline_id, m);
      }
    }
    return map;
  }, [metricsData]);

  const hasMetrics = metricsMap.size > 0;

  // Filter metrics by active funnel stage when funnel tabs are shown
  const activeMetricsItems = useMemo(() => {
    const items = Array.from(metricsMap.values());
    if (!hasMetadata) return items;
    const funnelIds = new Set(
      headlines
        .filter((h) => h.ad_funnel_stage?.funnel === activeFunnel)
        .map((h) => h.id)
        .filter((id): id is number => id != null),
    );
    return items.filter((m) => funnelIds.has(m.headline_id));
  }, [metricsMap, hasMetadata, headlines, activeFunnel]);

  const topHeadlineId = activeMetricsItems.length > 0
    ? activeMetricsItems.reduce((best, m) => (m.impressions > best.impressions ? m : best)).headline_id
    : null;

  const metricsLoadingRef = useRef(metricsLoading);
  metricsLoadingRef.current = metricsLoading;

  // #5789: delete via setHeadlinesRef + functional update, like every other handler here.
  // These are called from cells built inside the `columns` useMemo, which captures the
  // handler from mount — filtering the `headlines` prop directly replaced the whole list
  // with a mount-time snapshot, silently reverting any in-flight edits alongside the delete.
  const handleDelete = (headline: Headline) => {
    posthog?.capture('ad_headline_deleted', { campaign_id: campaign?.id });
    setHeadlinesRef.current((prev) => prev.filter((h) => h.id !== headline.id));
  };

  const handleDeleteConfirm = () => {
    if (!deletingHeadline) return;
    posthog?.capture('ad_headline_deleted', { campaign_id: campaign?.id });
    setHeadlinesRef.current((prev) => prev.filter((headline) => headline.id !== deletingHeadline.id));
    setDeletingHeadline(null);
  };

  const parseAddModalHeadlines = (text: string): string[] => {
    const seen = new Set<string>();
    return text
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .filter((s) => {
        const key = normalizeHeadline(s);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  // #5857: current headline count for the stage the Add modal targets; the Add button's
  // only disabled term is the 15 cap, which the always-visible banner in the modal explains.
  const addModalCurrentCount = hasMetadata
    ? headlines.filter((h) => h.ad_funnel_stage?.funnel === activeFunnel).length
    : headlines.length;
  const addModalAtCap = addModalCurrentCount >= 15;

  const handleAddModalSave = () => {
    const parsed = parseAddModalHeadlines(addModalText);
    if (parsed.length === 0) {
      setAddModalError('Required');
      return;
    }
    const valid = parsed
      .filter((s) => s.length <= 30 && !containsEmoji(s))
      .filter((s) => !headlines.some(
        (h) => normalizeHeadline(h.text) === normalizeHeadline(s) && (hasMetadata ? h.ad_funnel_stage?.funnel === activeFunnel : true),
      ));
    const toAdd = valid.slice(0, Math.max(0, 15 - addModalCurrentCount));
    if (toAdd.length === 0) {
      // The live banners above the button already list why each line was skipped.
      setAddModalError('No valid headlines');
      return;
    }
    posthog?.capture('ad_headlines_added_manually', { campaign_id: campaign?.id, count: toAdd.length });
    const newHeadlines: Headline[] = toAdd.map((text) => ({
      id: newTempHeadlineId(),
      text,
      ...(hasMetadata ? { ad_funnel_stage: { funnel: activeFunnel } as AdFunnelStage } : {}),
    }));
    setHeadlines([...newHeadlines, ...headlines]);
    setIsAddModalOpen(false);
  };

  // Build table rows from headlines (display order = newest first via reverse).
  const buildRows = (headlineList: Headline[]): HeadlineRow[] =>
    [...headlineList].reverse().map((headline) => ({
      headline,
      metrics: headline.id ? metricsMap.get(headline.id) : undefined,
      isTopPerformer: isLive && hasMetrics && headline.id === topHeadlineId,
      isHighlighted: highlightedIds?.has(headline.id ?? -1) ?? false,
    }));

  const columns = useMemo((): ColumnDef<HeadlineRow, unknown>[] => [
    {
      accessorFn: (row) => row.headline.text,
      id: 'headline',
      header: 'Headline',
      cell: function HeadlineCell({ row }: { row: { original: HeadlineRow } }) {
        const headline = row.original.headline;
        const [localVal, setLocalVal] = useState(headline.text);
        // #5857: short inline message ('Required' / 'No emojis' / 'Duplicate') rendered
        // where the counter sits, replacing the old toast for commit-time validation.
        const [cellError, setCellError] = useState<string | null>(null);
        const inputRef = useRef<HTMLInputElement>(null);
        // #4625-followup: capture the pre-edit text once per row so the blur
        // validator has something to compare against. Keystroke propagation
        // overwrites headline.text on every keypress, which would otherwise
        // make ``localVal !== headline.text`` always false in onBlur.
        const originalTextRef = useRef(headline.text);

        const isDuplicate = !!(duplicateTextsRef.current?.size &&
          duplicateTextsRef.current.has(normalizeHeadline(headline.text)));
        const hasError = !!cellError || isDuplicate;

        const propagate = (next: string) => {
          setHeadlinesRef.current((prev) =>
            prev.map((h) => (h.id === headline.id ? { ...h, text: next } : h)),
          );
        };

        return (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={localVal}
              onChange={(e) => {
                const next = e.target.value;
                setLocalVal(next);
                setCellError(null);
                // Propagate every keystroke so dirty calc fires immediately.
                propagate(next);
              }}
              onBlur={async () => {
                if (!localVal.trim()) {
                  setCellError('Required');
                  setLocalVal(originalTextRef.current);
                  propagate(originalTextRef.current);
                  setTimeout(() => setCellError(null), 5000);
                  return;
                }
                if (localVal !== originalTextRef.current) {
                  const error = await changeHeadline(headline, localVal, originalTextRef.current);
                  if (error) {
                    setCellError(error);
                    setLocalVal(originalTextRef.current);
                    propagate(originalTextRef.current);
                    setTimeout(() => setCellError(null), 5000);
                  } else {
                    originalTextRef.current = localVal;
                  }
                }
              }}
              maxLength={30}
              disabled={disabled}
              className={`text-sm text-gray-900 bg-transparent border rounded px-2 py-1.5 outline-none w-full transition-colors ${hasError ? 'border-red-400 bg-red-50' : 'border-transparent hover:border-gray-300 focus:border-[#290BD9] focus:bg-white'}`}
            />
            <span className={`text-xs shrink-0 ${hasError ? 'text-red-500' : 'text-gray-400'}`}>
              {cellError ?? `${localVal.length}/30`}
            </span>
          </div>
        );
      },
      meta: { className: 'px-4 min-w-[280px]' },
    },
    {
      id: 'type',
      header: 'Type',
      accessorFn: (row) => row.headline.type ?? '',
      sortingFn: (a, b) => headlineTypeRank(a.original.headline.type) - headlineTypeRank(b.original.headline.type),
      cell: ({ row }: { row: { original: HeadlineRow } }) => (
        <HeadlineTypeSelect
          type={row.original.headline.type}
          onChange={(t) => handleHeadlineTypeChange(row.original.headline, t)}
          disabled={disabled}
        />
      ),
    },
    {
      accessorFn: (row: HeadlineRow) => row.metrics?.performance_label ?? '',
      id: 'performanceBadge',
      header: 'Rating',
      cell: ({ row }: { row: { original: HeadlineRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoadingRef.current && metrics) {
          return <PerformanceBadge label={metrics.performance_label} />;
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    {
      accessorFn: (row: HeadlineRow) => row.metrics?.impressions ?? 0,
      id: 'impressions',
      header: 'Imp.',
      meta: { className: 'px-3' },
      cell: ({ row }: { row: { original: HeadlineRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoadingRef.current && metrics) {
          return (
            <span className={cn('text-xs', row.original.isTopPerformer ? 'text-[#047857] font-medium' : 'text-gray-500')}>
              {formatCompactNumber(metrics.impressions)}
            </span>
          );
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    {
      accessorFn: (row: HeadlineRow) => row.metrics?.ctr ?? 0,
      id: 'ctr',
      header: 'CTR',
      meta: { className: 'px-3' },
      cell: ({ row }: { row: { original: HeadlineRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoadingRef.current && metrics) {
          return (
            <span className={cn('text-xs', row.original.isTopPerformer ? 'text-[#047857] font-medium' : 'text-gray-500')}>
              {(metrics.ctr * 100).toFixed(1)}%
            </span>
          );
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    {
      accessorFn: (row: HeadlineRow) => row.metrics?.conversions ?? 0,
      id: 'conversions',
      header: 'Conv.',
      meta: { className: 'px-3' },
      cell: ({ row }: { row: { original: HeadlineRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoading && metrics && metrics.conversions > 0) {
          return (
            <span className={cn('text-xs', row.original.isTopPerformer ? 'text-[#047857] font-medium' : 'text-gray-500')}>
              {formatCompactNumber(metrics.conversions)}
            </span>
          );
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    ...(!disabled ? [{
      id: 'actions',
      // Right-aligned sort-arrow button matching the keywords tab's action-column
      // header — a single h-8/w-8 button with just the sort indicator (no entity
      // icon), sitting over the pin control in the cell.
      header: ({ column }) => {
        const dir = column.getIsSorted();
        const Icon = dir === 'asc' ? ArrowUp : dir === 'desc' ? ArrowDown : ArrowUpDown;
        return (
          // Mirror the cell layout ([pin] [delete], right-aligned): sort arrow over
          // the pin, spacer over the delete button.
          <div className='flex justify-end items-center gap-1'>
            <button
              type='button'
              onClick={() => column.toggleSorting()}
              className='h-8 w-8 inline-flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer'
              title='Sort by pin'
              aria-label='Sort by pin'
            >
              <Icon className={`h-3.5 w-3.5 ${dir ? 'text-gray-700' : 'text-gray-400'}`} />
            </button>
            <span className='h-8 w-8' aria-hidden='true' />
          </div>
        );
      },
      meta: { className: 'px-6 text-right' },
      // #5529: sortable by pin — pinned headlines (1→3) first, unpinned last.
      accessorFn: (row) => row.headline.pin_position ?? null,
      sortingFn: (a, b) =>
        (a.original.headline.pin_position ?? Number.MAX_SAFE_INTEGER) -
        (b.original.headline.pin_position ?? Number.MAX_SAFE_INTEGER),
      cell: function HeadlineActionsCell({ row }: { row: { original: HeadlineRow } }) {
        const headline = row.original.headline;
        const [isPinPopoverOpen, setIsPinPopoverOpen] = useState(false);

        return (
          <div className='flex justify-end items-center gap-1'>
            <Popover open={isPinPopoverOpen} onOpenChange={setIsPinPopoverOpen}>
              <PopoverTrigger asChild>
                <button className={`h-8 w-8 inline-flex items-center justify-center rounded transition-colors cursor-pointer ${headline.pin_position ? 'text-purple-800 bg-purple-100 hover:bg-purple-200' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}>
                  <div className='relative flex items-center justify-center'>
                    <Pin className='h-4 w-4' />
                    {headline.pin_position && (
                      <span className='absolute -top-1 -right-1 text-[9px] font-bold leading-none'>
                        {headline.pin_position}
                      </span>
                    )}
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className='w-40 p-1' align='end'>
                <p className='px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider'>Pin to position</p>
                {([1, 2, 3] as const).map((pos) => (
                  <button
                    key={pos}
                    className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-100 flex items-center gap-2 ${headline.pin_position === pos ? 'text-[#290BD9] font-medium' : 'text-gray-700'}`}
                    onClick={() => {
                      handlePinHeadline(headline, headline.pin_position === pos ? null : pos);
                      setIsPinPopoverOpen(false);
                    }}
                  >
                    <span>{pos === 1 ? '1st' : pos === 2 ? '2nd' : '3rd'} place</span>
                    {headline.pin_position === pos && <Pin className='h-3 w-3 text-[#290BD9] ml-auto' />}
                  </button>
                ))}
                {headline.pin_position && (
                  <>
                    <div className='my-1 border-t border-gray-100' />
                    <button
                      className='w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-gray-500'
                      onClick={() => {
                        handlePinHeadline(headline, null);
                        setIsPinPopoverOpen(false);
                      }}
                    >
                      Unpin
                    </button>
                  </>
                )}
              </PopoverContent>
            </Popover>
            <button
              // #4625: confirm-delete modal removed — Save Changes is the safety net.
              onClick={() => handleDelete(headline)}
              className='h-8 w-8 inline-flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer'
            >
              <Trash2 className='h-4 w-4' />
            </button>
          </div>
        );
      },
    } satisfies ColumnDef<HeadlineRow, unknown>] : []),
    // ``duplicateTexts`` intentionally omitted — cells read it via
    // ``duplicateTextsRef.current``. Including it would cause the columns
    // (and therefore HeadlineCell's function identity) to change every time
    // the parent clears the duplicate set, which happens on every keystroke
    // (#4625-followup: that was the second cause of focus loss while typing).
  ], [disabled, changeHeadline, handlePinHeadline, handleHeadlineTypeChange, setDeletingHeadline]);

  const buildSummaryFooter = (headlineList: Headline[]) => {
    const ids = new Set(headlineList.map((h) => h.id).filter((id): id is number => id != null));
    const items = Array.from(metricsMap.values()).filter((m) => ids.has(m.headline_id));
    if (items.length === 0) return undefined;
    const totalImpressions = items.reduce((s, m) => s + m.impressions, 0);
    const totalClicks = items.reduce((s, m) => s + m.clicks, 0);
    const totalConversions = items.reduce((s, m) => s + m.conversions, 0);
    const avgCtr = totalImpressions > 0 ? totalClicks / totalImpressions : 0;
    const hasActions = !disabled;
    return (
      <tr>
        <td className='px-4 py-2.5' colSpan={3}>
          <span className='text-xs font-semibold text-gray-500'>All Headlines</span>
        </td>
        <td className='px-3 py-2.5'>
          <span className='text-xs font-semibold text-gray-700'>{formatCompactNumber(totalImpressions)}</span>
        </td>
        <td className='px-3 py-2.5'>
          <span className='text-xs font-semibold text-gray-700'>{(avgCtr * 100).toFixed(1)}%</span>
        </td>
        <td className='px-3 py-2.5'>
          <span className='text-xs font-semibold text-gray-700'>{formatCompactNumber(totalConversions)}</span>
        </td>
        {hasActions && <td className='px-6 py-2.5' />}
      </tr>
    );
  };

  const renderTable = (headlineList: Headline[]) => {
    const rows = buildRows(headlineList);
    return (
      <DataTable<HeadlineRow>
        data={rows}
        columns={columns}
        variant='flush'
        pageSize={999}
        initialSorting={[{ id: 'type', desc: false }]}
        footerContent={buildSummaryFooter(headlineList)}
        emptyIcon={Type}
        emptyTitle='No Headlines'
        emptyDescription='Use the Add Headlines button above to add some.'
        // #4625-followup: stable row id by headline.id keeps React from
        // reusing DOM rows (and their stale localVal state) on delete.
        getRowId={(row) => String(row.headline.id)}
      />
    );
  };

  return (
    <div className='space-y-4'>
      {isGeneratingInBackground && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-[#C4B5FD] bg-[#EDE9FE] text-[#290BD9] text-sm">
          <Spinner size="sm" className="shrink-0" />
          <span>Headlines are being generated for new keywords. They will appear shortly.</span>
        </div>
      )}

      {hasMetadata ? (
        <Tabs
          value={activeFunnel}
          onValueChange={(v) => {
            const tab = v as FunnelStage;
            setActiveFunnel(tab);
            onFunnelChange?.(tab);
          }}
          className='w-full'
        >
          <TabsList className='grid w-full grid-cols-3'>
            {funnelStages.map((funnel) => {
              const isPaused = funnelStageData?.find((fs) => fs.funnel === funnel)?.is_paused;
              return (
                <TabsTrigger key={funnel} value={funnel} className='flex items-center gap-1.5'>
                  {getFunnelStageLabel(funnel)}
                  {isPaused && <PauseCircle className='h-3.5 w-3.5 text-amber-500' />}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {funnelStages.map((funnel) => (
            <TabsContent key={funnel} value={funnel}>
              {renderTable(headlines.filter((h) => h.ad_funnel_stage?.funnel === funnel))}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        renderTable(headlines)
      )}

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deletingHeadline}
        onClose={() => setDeletingHeadline(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Headline"
        description={`Are you sure you want to delete "${deletingHeadline?.text}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Add Headline Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className='sm:max-w-[480px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Headlines</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label>Headlines</Label>
                <FieldError message={addModalError} />
              </div>
              <Textarea
                value={addModalText}
                onChange={(e) => { setAddModalText(e.target.value); setAddModalError(undefined); }}
                placeholder={'One per line or comma-separated — max 30 characters each\nBest Running Shoes\nAffordable Athletic Footwear, Top Running Gear'}
                className='min-h-[140px] text-gray-900'
                error={!!addModalError}
                autoFocus
              />
            </div>
            {/* #5857: always-visible cap explainer — the Add button's only disabled term. */}
            {addModalAtCap && (
              <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm'>
                <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                <span>{addModalCurrentCount} of 15 headlines already added{hasMetadata ? ' in this stage' : ''}. Remove one before adding more.</span>
              </div>
            )}
            {(() => {
              const parsed = parseAddModalHeadlines(addModalText);
              if (parsed.length === 0) return null;

              const tooLong = parsed.filter((s) => s.length > 30);
              const withEmoji = parsed.filter((s) => containsEmoji(s));
              const dupes = parsed.filter((s) =>
                s.length <= 30 && !containsEmoji(s) &&
                headlines.some((h) => normalizeHeadline(h.text) === normalizeHeadline(s) && (hasMetadata ? h.ad_funnel_stage?.funnel === activeFunnel : true)),
              );
              const valid = parsed.filter(
                (s) => s.length <= 30 && !containsEmoji(s) && !dupes.includes(s),
              );
              const currentCount = hasMetadata
                ? headlines.filter((h) => h.ad_funnel_stage?.funnel === activeFunnel).length
                : headlines.length;
              const slots = Math.max(0, 15 - currentCount);
              const willAdd = valid.slice(0, slots);
              const capped = valid.length > slots;

              return (
                <div className='space-y-2 text-sm'>
                  {willAdd.length > 0 && (
                    <p className='text-gray-600'>
                      <span className='font-medium text-gray-900'>{willAdd.length}</span> headline{willAdd.length === 1 ? '' : 's'} will be added.
                    </p>
                  )}
                  {/* slots === 0 is covered by the always-visible cap banner above. */}
                  {capped && slots > 0 && (
                    <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207]'>
                      <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                      <span>{currentCount} already added — only the first {slots} will be saved (max 15).</span>
                    </div>
                  )}
                  {tooLong.length > 0 && (
                    <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207]'>
                      <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                      <span>{tooLong.length} skipped — over 30 characters: {tooLong.map((s) => `"${s.slice(0, 20)}…"`).join(', ')}</span>
                    </div>
                  )}
                  {withEmoji.length > 0 && (
                    <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207]'>
                      <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                      <span>{withEmoji.length} skipped — emoji not allowed.</span>
                    </div>
                  )}
                  {dupes.length > 0 && (
                    <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207]'>
                      <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                      <span>{dupes.length} skipped — already exist in this stage.</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsAddModalOpen(false)} className='leading-none'>
              Cancel
            </Button>
            <Button
              onClick={handleAddModalSave}
              // Tell-on-click (#5857): only the capacity cap disables Add (explained by the
              // always-visible banner); value validation surfaces inline on click instead.
              disabled={addModalAtCap}
              className='bg-[#290BD9] hover:bg-[#2309C5] leading-none'
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
